import { toFullName } from '@repo/toolkit/to-full-name'
import Button from '@repo/ui/button'
import DataTable from '@repo/ui/data-table'
import { AddUserIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API } from '../../../../api/apis.config'
import { addStaffsForMission } from '../../../../api/missions/add-staffs-to-mission'
import { cancelStaff } from '../../../../api/missions/cancel-staff'
import { confirmStaff } from '../../../../api/missions/confirm-staff'
import { getMissionAffectedStaffs } from '../../../../api/missions/get-affected-staffs'
import { getMission } from '../../../../api/missions/get-mission'
import {
  assignHotelToStaff,
  assignTicketToStaff,
  excludeBudgetToStaff,
  includeBudgetToStaff,
  unassignHotelToStaff,
  unassignTicketToStaff,
  updateBudgetToStaff
} from '../../../../api/missions/staff-mission-actions'
import { getTripFolder } from '../../../../api/trip-folder/get-trip-folder-details'
import { useDataManager } from '../../../../lib/hooks/use-data-manager'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import { EmailModal } from '../../common/email-modal'
import MissionStaffCancellationForm from '../mission-staff-cancellation-form'
import MissionStaffCancellationModal from '../mission-staff-cancellation-modal'
import MissionStaffConfirmationModal from '../mission-staff-confirmation-modal'
import { MissionStaffList } from '../mission-staff-list'
import { MissionStaff } from './mission-staff-table'
import { affectedListColumns, AffectedUser, mapStaffToAffectedUser } from './utils'

type Props = MissionStaff

const AffectedStaffList: React.FC<Props> = ({ missionId, mode = 'edit' }) => {
  const queryClient = useQueryClient()
  const { modalState, toggleModal, selectedData, closeModel, closeAll } = useModalManager<
    'add' | 'delete' | 'confirm' | 'send-affection-email' | 'send-deaffection-email',
    AffectedUser
  >(['add', 'delete', 'confirm', 'send-affection-email', 'send-deaffection-email'])
  const { data, setData, updateFields } = useDataManager<AffectedUser>([])

  const { isPending } = useQuery({
    queryKey: ['mission-enrolled-staffs', missionId],
    queryFn: async () => {
      const res = await getMissionAffectedStaffs(missionId)
      setData(res.map(mapStaffToAffectedUser))
      return res
    }
  })

  const { data: mission } = useQuery({
    queryKey: ['missions', missionId],
    queryFn: () => getMission(missionId),
    enabled: !!missionId
  })

  const { data: tripfolder } = useQuery({
    queryKey: ['trip-folder', mission?.tripFolderId],
    queryFn: () => {
      if (mission?.tripFolderId) return getTripFolder(mission.tripFolderId)
      return null
    },
    enabled: !!mission?.tripFolderId
  })

  const { mutate: addStaffsToMissionMutate, isPending: isAddStaffsForMissionPending } =
    useMutation({
      mutationFn: addStaffsForMission,
      onError: showAxiosError,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['mission-enrolled-staffs', missionId] })
        closeModel('add')
        toast.success('Encadrants affectés avec succès')
      }
    })

  const { mutate: confirmStaffMutation, isPending: isConfirmStaffPending } = useMutation({
    mutationFn: confirmStaff,
    onError: showAxiosError,
    onSuccess: () => {
      closeModel('confirm')
      queryClient.invalidateQueries({ queryKey: ['mission-enrolled-staffs', missionId] })
      toast.success('Encadrant confirmé avec succès')

      if (tripfolder && tripfolder.locked) toggleModal('send-affection-email')
    }
  })

  const { mutate: cancelStaffMutation, isPending: isCancelStaffPending } = useMutation({
    mutationFn: cancelStaff,
    onError: showAxiosError,
    onSuccess: () => {
      closeModel('delete')
      queryClient.invalidateQueries({ queryKey: ['mission-enrolled-staffs', missionId] })
      toast.success('Encadrant annulé avec succès')

      if (tripfolder && tripfolder.locked) toggleModal('send-deaffection-email')
    }
  })

  const columns = affectedListColumns(missionId, mode, {
    assignHotel: async (params) => {
      await assignHotelToStaff({ staffId: params.id, id: missionId })
      toast.success('Hôtel affecté avec succès')
    },
    unassignHotel: async (params) => {
      await unassignHotelToStaff({ staffId: params.id, id: missionId })
      toast.success('Hôtel retiré avec succès')
    },
    assignTicket: async (params) => {
      await assignTicketToStaff({ staffId: params.id, id: missionId })
      toast.success('Ticket affecté avec succès')
    },
    unassignTicket: async (params) => {
      await unassignTicketToStaff({ staffId: params.id, id: missionId })
      toast.success('Ticket retiré avec succès')
    },
    includeBudget: async (params) => {
      await includeBudgetToStaff({ staffId: params.id, id: missionId })
      toast.success('Budget inclus avec succès')
    },
    excludeBudget: async (params) => {
      await excludeBudgetToStaff({ staffId: params.id, id: missionId })
      toast.success('Budget exclus avec succès')
    },
    updateBudget: async (params) => {
      await updateBudgetToStaff({ ...params, staffId: params.id, id: missionId })
      toast.success('Budget mis à jour avec succès')
    },
    updateFields,
    onConfirm: (item) => toggleModal('confirm', item),
    onCancel: (item) => toggleModal('delete', item)
  })
    .filter((column) => column.version.includes('STAFF'))
    .filter((column) => column.visible !== false)

  if (isPending) return <Loader center />
  if (mode === 'view' && data?.length === 0) return null

  return (
    <ActionTableWrapper
      title={'Encadrants affectés'}
      actions={[
        mode === 'edit' ? (
          <Button
            key={'add'}
            type="button"
            icon={AddUserIcon}
            label={'Affecter des encadrants'}
            variant={'secondary'}
            onClick={() => toggleModal('add')}
          />
        ) : null
      ]}
    >
      <DataTable<AffectedUser>
        columns={columns}
        data={data ?? []}
        paginated={false}
        rowCount={data?.length ?? 0}
      />

      <Modal
        isOpen={modalState.add}
        size="large"
        icon={AddUserIcon}
        title="Affecter des encadrants"
        onCancel={() => toggleModal('add')}
        hideActions
      >
        <MissionStaffList
          className={MODAL_BODY_CLASSNAME}
          missionId={missionId}
          isLoading={isAddStaffsForMissionPending}
          onCancel={() => toggleModal('add')}
          onSubmit={(staffs) => {
            addStaffsToMissionMutate({
              id: missionId,
              staffIds: staffs.map((staff) => staff.id)
            })
          }}
        />
      </Modal>

      {selectedData && (
        <MissionStaffCancellationModal
          onCancel={() => toggleModal('delete')}
          isOpen={modalState.delete}
        >
          <MissionStaffCancellationForm
            isLoading={isCancelStaffPending}
            onCancel={() => toggleModal('delete')}
            onConfirm={(cancellationReason) =>
              cancelStaffMutation({ id: missionId, staffId: selectedData.id, cancellationReason })
            }
          />
        </MissionStaffCancellationModal>
      )}
      {selectedData && (
        <MissionStaffConfirmationModal
          onConfirm={() => confirmStaffMutation({ id: missionId, staffId: selectedData.id })}
          onCancel={() => toggleModal('confirm')}
          isOpen={modalState.confirm}
          isLoading={isConfirmStaffPending}
          name={toFullName(selectedData.firstName, selectedData.lastName)}
        />
      )}

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffAffectionNotification.url}
        label="Vous avez affecté un AAT à une mission dont le dossier voyage est verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-affection-email']}
        closeModal={() => closeAll()}
      />

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffDeaffectionNotification.url}
        label="Vous avez désaffecté un AAT d'une mission confirmée. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-deaffection-email']}
        closeModal={() => closeAll()}
      />
    </ActionTableWrapper>
  )
}

export default AffectedStaffList
